import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import Reveal from './Reveal';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

const CheckCircle = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" style={{ stroke: 'currentColor', strokeWidth: 1.7 }}>
    <circle cx="8" cy="8" r="6.4" strokeOpacity=".4" />
    <path d="M5.2 8.3 7 10.1l3.8-4" />
  </svg>
);

const verifiedBadge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.4rem',
  color: 'var(--accent)',
  ...mono,
  fontSize: '.625rem',
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  padding: '.9rem 0',
  borderTop: '1px solid var(--line)',
};

const titleStyle: React.CSSProperties = { fontSize: '.9375rem', color: 'var(--ink)' };
const subStyle: React.CSSProperties = { ...mono, fontSize: '.6875rem', color: 'var(--ink-3)', marginTop: '.2rem' };
const titleLine: React.CSSProperties = { ...titleStyle, display: 'flex', alignItems: 'center', gap: '.55rem' };
const algoToken: React.CSSProperties = { ...mono, fontSize: '.8125rem', color: 'var(--ink)', whiteSpace: 'nowrap' };

const DIAL_W = 6; // px — reel dial width
const TOKEN_W = '10.75em'; // reel window width — fits the longest phrase, "SLH-DSA signature"

const Dial = () => (
  <svg width={DIAL_W} height="9" viewBox="0 0 6 9" fill="var(--accent)" aria-hidden style={{ flexShrink: 0 }}>
    <path d="M0 0l6 4.5L0 9z" />
  </svg>
);

const EcdsaAuthRow: React.FC = () => (
  <div style={rowStyle}>
    <div>
      <div style={titleLine}>
        <Dial />
        <span style={algoToken}>ECDSA signature</span>
      </div>
      <div style={subStyle}>Classical authorization</div>
    </div>
    <span style={verifiedBadge}>
      <CheckCircle />
      verified
    </span>
  </div>
);

// NIST post-quantum signature standards — cycled to show QSSN is algorithm-agile.
const PQC_ALGOS = ['ML-DSA', 'SLH-DSA', 'FN-DSA'];
const REEL_ROW = 20; // px height of one slot on the wheel
const REEL = [...PQC_ALGOS, ...PQC_ALGOS, ...PQC_ALGOS];

const PqcAuthRow: React.FC = () => {
  const reduce = useReducedMotion();
  const len = PQC_ALGOS.length;
  const [pos, setPos] = useState(len); // run in the middle copy so neighbours always exist
  const [animated, setAnimated] = useState(true);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setAnimated(true);
      setPos((p) => p + 1);
    }, 2400);
    return () => clearInterval(id);
  }, [reduce]);

  // once the wheel rolls into the next copy, snap back invisibly (identical labels)
  const handleRest = () => {
    if (pos >= len * 2) {
      setAnimated(false);
      setPos((p) => p - len);
    }
  };

  const boxH = REEL_ROW * 1.8;
  const trackY = boxH / 2 - REEL_ROW / 2 - pos * REEL_ROW;

  return (
    <div style={rowStyle}>
      <div>
        <div style={titleLine}>
          <Dial />
          <span
            aria-label={`${PQC_ALGOS[pos % len]} signature`}
            style={{
              position: 'relative',
              display: 'inline-block',
              width: TOKEN_W,
              height: boxH,
              overflow: 'hidden',
              WebkitMaskImage: 'linear-gradient(180deg,transparent 0%,#000 22%,#000 78%,transparent 100%)',
              maskImage: 'linear-gradient(180deg,transparent 0%,#000 22%,#000 78%,transparent 100%)',
              ...mono,
              fontSize: '.8125rem',
            }}
          >
            <motion.span
              animate={{ y: trackY }}
              transition={animated ? { duration: 0.5, ease: [0.4, 0, 0.2, 1] } : { duration: 0 }}
              onAnimationComplete={handleRest}
              style={{ position: 'absolute', left: 0, right: 0, top: 0, display: 'block' }}
            >
              {REEL.map((a, idx) => (
                <span key={idx} style={{ display: 'flex', alignItems: 'center', height: REEL_ROW, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
                  {a} signature
                </span>
              ))}
            </motion.span>
          </span>
        </div>
        <div style={subStyle}>Post-quantum authorization</div>
      </div>
      <span style={verifiedBadge}>
        <CheckCircle />
        verified
      </span>
    </div>
  );
};

const AuthorizationInAction: React.FC = () => (
  <section style={{ background: 'var(--bg-alt)', padding: '7rem 1.5rem' }}>
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Reveal style={{ textAlign: 'center', maxWidth: '46rem', margin: '0 auto 3.5rem' }}>
        <h2
          style={{
            margin: 0,
            fontWeight: 500,
            fontSize: 'clamp(1.9rem,3.4vw,2.9rem)',
            lineHeight: 1.08,
            letterSpacing: '-.02em',
            color: 'var(--ink)',
            textWrap: 'balance',
          } as React.CSSProperties}
        >
          No privileged action executes without quantum-safe authorization.
        </h2>
      </Reveal>

      <Reveal delay={80} style={{ maxWidth: 460, margin: '0 auto' }}>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 16,
            boxShadow: '0 1px 2px rgba(16,18,24,.04),0 30px 60px -34px rgba(16,18,24,.28)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span style={{ ...mono, fontSize: '.6875rem', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              Authorization request
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.45rem', ...mono, fontSize: '.625rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--accent)' }}>
              <CheckCircle />
              cleared
            </span>
          </div>
          <div style={{ padding: '1.25rem 1.25rem 1.4rem' }}>
            <div style={{ ...mono, fontSize: '.625rem', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
              Privileged action
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem', marginTop: '.5rem' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-.01em' }}>Treasury mint</span>
              <span style={{ ...mono, fontSize: '.875rem', color: 'var(--ink-2)' }}>5,000,000 USD</span>
            </div>

            <div style={{ marginTop: '1.4rem' }}>
              <EcdsaAuthRow />
            </div>
            <PqcAuthRow />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                marginTop: '1rem',
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent-soft)',
                borderRadius: 10,
                padding: '.8rem 1rem',
              }}
            >
              <span style={{ fontSize: '.875rem', fontWeight: 500, color: 'var(--ink)' }}>Single-use keys</span>
              <span style={{ ...mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--accent)' }}>
                retired after signing
              </span>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default AuthorizationInAction;
